import React, {
  createContext,
  useEffect,
  useState,
  useContext,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import { Auth, Hub, Amplify } from "aws-amplify";
import awsconfig from "./aws-exports";

// Estilos e Contextos
import "./AppV1.scss";
import SystemContext, { SystemProvider } from "./context/SystemContext";
import { ActivitiesProvider } from "./context/ActivitiesContext";
import { THEME } from "./configs/theme";

// Configurações Modulares Separadas
import {
  app,
  registerServiceWorker,
  initPushNotifications,
  checkMaintenanceStatus,
} from "./configs/firebase";
import { syncTrackingUser } from "./configs/tracking";
import {
  checkSessionLocal,
  checkCacheLocal,
  listenFocusBlurSession,
} from "@helpers/cacheManagerHelper";
import { getAppBackground } from "./helpers/themeSelector";
import { setAnalyticsDeviceOpenNotify } from "./helpers/analyticsHelpers";

// Componentes Reutilizáveis
import CodeCapture from "@components/utils/CodeCapture";
import AppRoutes from "./router/AppRoutes";
import Footer from "@hoc/Layout/Footer/Footer";

// Redux Actions e Sagas
import {
  initSessionActive,
  resetSession,
  initFetchActivities,
  systemLoad,
  resetTasks,
} from "./store/actions";
import { refreshTokenSaga } from "./store/sagas/_LocalRefreshToken";

// Inicializações Globais Síncronas
Amplify.configure(awsconfig);
registerServiceWorker();

export const FirebaseContext = createContext(null);

const App = () => {
  const dispatch = useDispatch();
  const { session, activeUser } = useSelector((state) => state.session);
  const { myUsers } = useSelector((state) => state.myUsers);
  const configs = useSelector((state) => state.configs);
  const { isRegister } = useContext(SystemContext);
  const [userSession, setUserSession] = useState(null);
  const [forceUpdateSession, setForceUpdateSession] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [messaging, setMessaging] = useState(null);
  const [urlParam, setUrlParam] = useState(null);
  const isAuthenticated = !!session;
  const hasPrompted = useRef(false);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    checkSessionLocal();
    listenFocusBlurSession();
    initPushNotifications(setMessaging);
    dispatch(resetTasks());

    const url = new URL(window.location.href);
    setUrlParam(url.searchParams.get("pushnotify"));
  }, [dispatch]);

  useEffect(() => {
    if (process.env.REACT_APP_STAGE === "dev" && !hasPrompted.current) {
      const acessoLiberado = localStorage.getItem("dev_access_granted");
      hasPrompted.current = true;
      if (!acessoLiberado) {
        const senha = prompt(
          "🔐 Ambiente de desenvolvimento restrito. Digite a senha:",
        );
        if (senha !== process.env.REACT_APP_DEV_PASSWORD) {
          alert("❌ Senha incorreta.");
          window.location.href = "https://novo.afinandoocerebro.com.br/painel";
        } else {
          localStorage.setItem("dev_access_granted", "true");
        }
      }
    }
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      const maintenanceActive = await checkMaintenanceStatus();
      setIsMaintenance(maintenanceActive);
      setConfigLoaded(true);
    };
    fetchStatus();
  }, []);

  global.updateSession = () => {
    setForceUpdateSession((prev) => prev + 1);
    // dispatch(resetSession());
    // setUserSession(null);
  };

  useEffect(() => {
    const checkUserSession = () => {
      if (session) {
        setIsLoading(false);
        return;
      }
      dispatch(systemLoad(0));
      Auth.currentAuthenticatedUser()
        .then((cognitoUser) => {
          setUserSession(cognitoUser);
          dispatch(systemLoad(1));
        })
        .catch((err) => {
          setUserSession(null);
          dispatch(systemLoad(2));
          setIsLoading(false);
        });
    };

    checkUserSession();

    const hubListener = Hub.listen("auth", (data) => {
      const { event } = data.payload;

      if (event === "signIn") {
        checkUserSession();
      } else if (event === "signOut") {
        setUserSession(null);
        dispatch(systemLoad(2));
      }
    });

    // Limpa o listener ao desmontar o componente para evitar vazamento de memória
    return () => hubListener();
  }, [dispatch]);

  useEffect(() => {
    if (!userSession) return;
    const dayOfWeek = new Date().getDay();

    if (!configs?.dailyReward || configs.dailyReward.day !== dayOfWeek) {
      dispatch({
        type: "DAILY_REWARD_INFO",
        payload: { dailyReward: { day: dayOfWeek, claimed: false } },
      });
    }
  }, [userSession, configs?.dailyReward?.day, dispatch]);

  useEffect(() => {
    if (!userSession) return;

    const shouldInit =
      (!session && !isAuthenticated) || forceUpdateSession === 1;

    if (shouldInit && !isInitializingRef.current) {
      isInitializingRef.current = true;
      console.log("Initializing session and fetching activities...");

      dispatch(initSessionActive(userSession));
      // dispatch(initFetchActivities());

      if (forceUpdateSession === 1) {
        refreshTokenSaga();
        setForceUpdateSession(2);
      }
    }
  }, [userSession, session, isAuthenticated, forceUpdateSession, dispatch]);

  useEffect(() => {
    if (session || isAuthenticated) {
      isInitializingRef.current = false;
    }
  }, [session, isAuthenticated]);

  useEffect(() => {
    if (session) {
      syncTrackingUser(session);
      setIsLoading(false);
      isInitializingRef.current = false;
      if (forceUpdateSession === 2) {
        setForceUpdateSession(false);
      }
    }
  }, [session, forceUpdateSession]);

  useEffect(() => {
    if (session && session.my_user && urlParam) {
      setAnalyticsDeviceOpenNotify({ name: urlParam });
    }
  }, [urlParam, session]);

  if (!configLoaded) return null;

  return (
    <HelmetProvider>
      <ThemeProvider theme={THEME}>
        <FirebaseContext.Provider value={app}>
          <div
            className="App"
            style={{ backgroundImage: `url(${getAppBackground(isRegister)})` }}
          >
            <SystemProvider>
              <ActivitiesProvider>
                <CodeCapture />
                <AppRoutes
                  isAuthenticated={isAuthenticated}
                  isMaintenance={isMaintenance}
                  session={session}
                  myUsers={myUsers}
                  activeUser={activeUser}
                  check_cache_local={checkCacheLocal}
                  isLoading={isLoading}
                />
              </ActivitiesProvider>
              <Footer />
            </SystemProvider>
          </div>
        </FirebaseContext.Provider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
